/**
 * Returns the original indices of the given yAxis array sorted in visual left-to-right order.
 *
 * Sort order:
 * - Left axes come before right axes.
 * - Within left axes: descending offset (outermost first).
 * - Within right axes: ascending offset (innermost first, outermost last).
 *
 * @param optionsYAxis The ECharts yAxis array from chart options
 * @returns Original indices in visual left-to-right order
 */
export function getSortedYAxisIndices(
  optionsYAxis: Array<{ position?: string; offset?: number }>
): number[] {
  return optionsYAxis
    .map((axis, i) => ({
      index: i,
      position: axis.position ?? "left",
      offset: axis.offset ?? 0
    }))
    .sort((a, b) => {
      if (a.position !== b.position) {
        return a.position === "left" ? -1 : 1;
      }
      if (a.position === "left") {
        return b.offset - a.offset; // descending offset: outermost left axis first
      }
      return a.offset - b.offset; // ascending offset: innermost right axis first
    })
    .map((item) => item.index);
}

/**
 * Returns the index of the innermost y-axis, always preferring left-positioned axes.
 * Among left axes, picks the one with the smallest offset (innermost).
 * Falls back to the innermost right axis only when no left axes exist.
 */
export function getInnermostYAxisIndex(
  yAxis: Array<{ position?: string; offset?: number }>
): number {
  const innermostOnSide = (position: string) =>
    yAxis.reduce((best, axis, i) => {
      if ((axis.position ?? "left") !== position) return best;
      if (best === -1) return i;
      return (axis.offset ?? 0) < (yAxis[best].offset ?? 0) ? i : best;
    }, -1);

  const innermostLeft = innermostOnSide("left");
  return innermostLeft !== -1 ? innermostLeft : innermostOnSide("right");
}

/**
 * Returns the original indices of the given xAxis array sorted in visual top-to-bottom order.
 *
 * Sort order:
 * - Top axes come before bottom axes (primary position first).
 * - Within top axes: descending offset (outermost first).
 * - Within bottom axes: ascending offset (innermost first).
 *
 * @param optionsXAxis The ECharts xAxis array from chart options
 * @returns Original indices in visual top-to-bottom order (top first, then bottom)
 */
export function getSortedXAxisIndices(
  optionsXAxis: Array<{ position?: string; offset?: number }>
): number[] {
  return optionsXAxis
    .map((axis, i) => ({
      index: i,
      position: axis.position ?? "bottom",
      offset: axis.offset ?? 0
    }))
    .sort((a, b) => {
      if (a.position !== b.position) {
        return a.position === "top" ? -1 : 1;
      }
      if (a.position === "top") {
        return b.offset - a.offset; // descending offset: outermost top axis first
      }
      return a.offset - b.offset; // ascending offset: innermost bottom axis first
    })
    .map((item) => item.index);
}
